import { Confirmation, Icons, TicketDetails } from '@tabeo/scarf'
import ACL from 'components/ACL'
import moment from 'moment'
import PropTypes from 'prop-types'
import React from 'react'
import { useTicket } from 'resources/Ticket'
import { ticketTypeConfigs } from 'resources/Ticket/types'

const {
  ActionButton,
  Actions,
  Meta,
  MetaItem,
  Sidebar: BaseSidebar,
} = TicketDetails

const formatDateTime = date => {
  const formatted = moment(date).format('DD MMM YYYY hh:mm A')
  const dayPeriod = formatted.slice(-2).toLocaleLowerCase()
  return formatted.slice(0, -2) + dayPeriod
}

function Sidebar({ closeModal, ticket, ...rest }) {
  const {
    resource: { toggleField, remove },
  } = useTicket()

  const {
    isStarred,
    isResolved,
    createdAt,
    updatedAt,
    resolvedAt,
    creator,
    resolver,
    type,
  } = ticket

  const ticketTypeConfig = ticketTypeConfigs[type] || {}

  return (
    <BaseSidebar
      pt={[0, 10]}
      flexDirection={['column-reverse', 'column']}
      {...rest}
      data-testid="ticket-sidebar"
    >
      <ACL deny={['auditor_wa']}>
        <Actions>
          {!isResolved && !ticketTypeConfig.isStarHidden && (
            <ActionButton
              textAlign="left"
              leftIcon={({ fill, ...rest }) => (
                <Icons.Star
                  active={!isStarred}
                  {...rest}
                  fill={isStarred ? fill : 'default.yellow'}
                />
              )}
              onClick={() => toggleField('isStarred')}
              contentWrapperProps={{
                justifyContent: ['center', 'space-between'],
              }}
              childrenWrapperProps={{ flex: [0, 1] }}
            >
              {isStarred ? 'Unstar' : 'Star'}
            </ActionButton>
          )}
          {!ticketTypeConfig.isDeleteHidden && (
            <Confirmation
              content="Are you sure you want to delete this ticket?"
              confirmText="Delete"
              onConfirm={() => {
                remove()
                closeModal()
              }}
            >
              <ActionButton
                textAlign="left"
                leftIcon={props => <Icons.Trash {...props} />}
                contentWrapperProps={{
                  justifyContent: ['center', 'space-between'],
                }}
                childrenWrapperProps={{ flex: [0, 1] }}
              >
                Delete
              </ActionButton>
            </Confirmation>
          )}

          <ActionButton
            textAlign="left"
            leftIcon={props =>
              isResolved ? (
                <Icons.Revert {...props} />
              ) : (
                <Icons.MarkAsDone {...props} />
              )
            }
            onClick={() => toggleField('isResolved')}
            contentWrapperProps={{
              justifyContent: ['center', 'space-between'],
            }}
            childrenWrapperProps={{ flex: [0, 1] }}
          >
            {isResolved ? 'Unresolve' : 'Resolve'}
          </ActionButton>
        </Actions>
      </ACL>
      <Meta mt={[isResolved ? 0 : 5, 6]} mb={[5, 0]}>
        <MetaItem title="Created by" order={[4, 0]} flexBasis={['33%', '100%']}>
          {creator.firstName} {creator.lastName}
        </MetaItem>

        <MetaItem
          title="Created on"
          order={[0, 1]}
          flexBasis={['33%', '100%']}
          pr={2}
        >
          {formatDateTime(createdAt)}
        </MetaItem>
        {!isResolved && (
          <MetaItem
            title="Updated on"
            order={[2, 2]}
            flexBasis={['33%', '100%']}
            pr={2}
          >
            {formatDateTime(updatedAt)}
          </MetaItem>
        )}
      </Meta>
      {isResolved && resolver.id && (
        <Meta mt={[5, 0]} mb={[0, 0]}>
          <MetaItem
            title="Resolved by"
            order={[4, 0]}
            flexBasis={['33%', '100%']}
          >
            {resolver.firstName} {resolver.lastName}
          </MetaItem>

          <MetaItem
            title="Resolved on"
            order={[0, 1]}
            flexBasis={['33%', '100%']}
            pr={2}
          >
            {formatDateTime(resolvedAt)}
          </MetaItem>
        </Meta>
      )}
    </BaseSidebar>
  )
}

Sidebar.propTypes = {
  closeModal: PropTypes.func.isRequired,
  ticket: PropTypes.object.isRequired,
}

export default Sidebar
